import { useEffect, useState } from 'react';
import type { Device } from '@cornerstone/shared';
import { API_BASE } from '../client/config';

// =============================================================================
// Device health flags derived from the heartbeat (2026-09-22 웹/APK 기기 리스크 작업).
// One place decides what counts as a problem so the list badges, the detail
// page and future mail alerts agree. All thresholds are plain numbers here.
// =============================================================================

export type PlayerBuild = { bundle: string | null; version: string | null };
export type HealthFlag = { key: 'bundle' | 'rtc' | 'storage' | 'diagnostic' | 'wifi'; label: string; detail: string; severity: 'warn' | 'error' };

export const RTC_OFFSET_WARN_MS = 60_000;
export const STORAGE_FREE_WARN_BYTES = 500 * 1024 * 1024;
export const STORAGE_FREE_WARN_RATIO = 0.1;
/** 최근 1시간 Wi-Fi 끊김이 이 횟수 이상이면 불안정으로 본다. */
export const WIFI_DROPS_WARN = 3;

const bytes = (value: number) => value >= 1024 ** 3 ? `${(value / 1024 ** 3).toFixed(1)} GB` : `${Math.round(value / 1024 ** 2)} MB`;

export function deviceHealthFlags(device: Pick<Device, 'nativeStatus' | 'online'>, serverBuild: PlayerBuild | null): HealthFlag[] {
  const status = device.nativeStatus ?? {};
  const flags: HealthFlag[] = [];
  if (serverBuild?.bundle && status.webBundle && status.webBundle !== serverBuild.bundle) {
    flags.push({ key: 'bundle', label: '번들 구버전', severity: 'warn',
      detail: `기기 ${status.webBundle.replace('assets/', '')} · 서버 ${serverBuild.bundle.replace('assets/', '')}${status.webBundleStale ? ' · 자동 갱신 3회 실패' : ' · 다음 하트비트에 자동 갱신'}` });
  }
  if (typeof status.rtcOffsetMs === 'number' && Math.abs(status.rtcOffsetMs) > RTC_OFFSET_WARN_MS) {
    flags.push({ key: 'rtc', label: 'RTC 오차', severity: 'error', detail: `기기 시계가 서버와 ${Math.round(Math.abs(status.rtcOffsetMs) / 1000)}초 ${status.rtcOffsetMs > 0 ? '빠릅니다' : '느립니다'} · 전원 예약·시계 위젯이 어긋납니다` });
  }
  if (typeof status.storageFreeBytes === 'number' && (status.storageFreeBytes < STORAGE_FREE_WARN_BYTES
    || typeof status.storageTotalBytes === 'number' && status.storageTotalBytes > 0 && status.storageFreeBytes / status.storageTotalBytes < STORAGE_FREE_WARN_RATIO)) {
    flags.push({ key: 'storage', label: '저장 공간 부족', severity: 'warn', detail: `남은 공간 ${bytes(status.storageFreeBytes)} · 오프라인 재생본을 다 받지 못할 수 있습니다` });
  }
  if (typeof status.wifiDisconnects1h === 'number' && status.wifiDisconnects1h >= WIFI_DROPS_WARN) {
    flags.push({ key: 'wifi', label: 'Wi-Fi 불안정', severity: 'warn',
      detail: `최근 1시간 끊김 ${status.wifiDisconnects1h}회 · 대역 전환 ${status.wifiBandSwitches1h ?? 0}회 · 상세의 Wi-Fi 진단 참고` });
  }
  if (status.diagnosticMode === true) {
    flags.push({ key: 'diagnostic', label: '진단 빌드', severity: 'warn', detail: '-diag APK: 내장 실행본에 고정되어 서버 번들 갱신을 받지 않습니다 · 정식 APK로 교체' });
  }
  return flags;
}

let cached: { at: number; build: PlayerBuild } | null = null;
/** Current server Player bundle, shared by every badge on the page; refreshed each minute. */
export function useServerPlayerBuild(): PlayerBuild | null {
  const [build, setBuild] = useState<PlayerBuild | null>(cached?.build ?? null);
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (cached && Date.now() - cached.at < 60_000) { setBuild(cached.build); return; }
      try {
        const response = await fetch(`${API_BASE}/api/player-build`, { credentials: 'include', cache: 'no-store' });
        if (!response.ok) return;
        const next = await response.json() as PlayerBuild;
        cached = { at: Date.now(), build: next };
        if (active) setBuild(next);
      } catch { /* badge simply stays hidden */ }
    };
    void load(); const timer = window.setInterval(() => void load(), 60_000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  return build;
}

const TONE = { warn: 'border-amber-500/50 text-amber-600 bg-amber-500/10', error: 'border-red/50 text-red bg-red/10' } as const;
export function DeviceHealthBadges({ device, serverBuild, compact = false }: { device: Pick<Device, 'nativeStatus' | 'online'>; serverBuild: PlayerBuild | null; compact?: boolean }) {
  const flags = deviceHealthFlags(device, serverBuild);
  if (!flags.length) return null;
  return <span className="inline-flex flex-wrap gap-1" data-device-health={flags.map(flag => flag.key).join(' ')}>
    {flags.map(flag => <span key={flag.key} title={flag.detail} className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap ${TONE[flag.severity]}`}>
      {flag.label}{!compact && <span className="ml-1 opacity-80">· {flag.detail}</span>}
    </span>)}
  </span>;
}
